import { useNavigate } from 'react-router-dom';
import ArrowLeftIcon from '@app/assets/icons/arrow-left.svg?react';

interface Section {
  title: string;
  content: React.ReactNode;
}

interface DocumentationArticleProps {
  title: string;
  description: string;
  timeToComplete: string;
  requirements: string[];
  sections: Section[];
  faq?: { question: string; answer: string }[];
}

export const DocumentationArticle = ({
  title,
  description,
  timeToComplete,
  requirements,
  sections,
  faq,
}: DocumentationArticleProps) => {
  const navigate = useNavigate();

  return (
    <div className="p-4 sm:p-6 lg:p-10 lg:mx-6">
      <button
        onClick={() => navigate('/documentation')}
        className="flex items-center gap-2 text-base-coolGray hover:text-base-raisinBlack mb-6 transition-colors"
      >
        <ArrowLeftIcon className="w-5 h-5" />
        <span>Back to Documentation</span>
      </button>

      <div className="bg-white rounded-lg p-6 lg:p-8">
        <h1 className="text-2xl lg:text-3xl font-bold text-base-raisinBlack mb-4">
          {title}
        </h1>

        <div className="mb-8">
          <h2 className="text-lg font-semibold text-base-raisinBlack mb-2">
            Purpose
          </h2>
          <p className="text-base-coolGray">{description}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 p-4 bg-base-antiFlash rounded-lg">
          <div>
            <h3 className="font-semibold text-base-raisinBlack mb-1">
              Time to complete
            </h3>
            <p className="text-base-coolGray">{timeToComplete}</p>
          </div>
          <div>
            <h3 className="font-semibold text-base-raisinBlack mb-1">
              You'll need
            </h3>
            <ul className="text-base-coolGray">
              {requirements.map((req, index) => (
                <li key={index}>{req}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index}>
              <h2 className="text-xl font-semibold text-base-raisinBlack mb-4">
                {section.title}
              </h2>
              <div className="text-base-coolGray prose prose-sm max-w-none">
                {section.content}
              </div>
            </div>
          ))}
        </div>

        {faq && faq.length > 0 && (
          <div className="mt-10 pt-8 border-t border-gray-100">
            <h2 className="text-xl font-semibold text-base-raisinBlack mb-6">
              Common Questions
            </h2>
            <div className="space-y-4">
              {faq.map((item, index) => (
                <div key={index}>
                  <h3 className="font-semibold text-base-raisinBlack mb-1">
                    {item.question}
                  </h3>
                  <p className="text-base-coolGray">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10 pt-8 border-t border-gray-100">
          <h2 className="text-xl font-semibold text-base-raisinBlack mb-4">
            Still Need Help?
          </h2>
          <p className="text-base-coolGray mb-4">
            Use the Helpcenter to contact support.
          </p>
          <p className="text-base-coolGray">
            When reaching out, include your business name and a short description
            of what you're trying to do.
          </p>
        </div>
      </div>
    </div>
  );
};
